// helpers/IrradianceJobs.js
import { IRR_CACHE, getOrCreateCache } from "./IrradianceCache";
import { POLYGONS } from "../ThreeJs/Global/variables";

// same as API.js uses
export const LIDAR_URL = "https://lider.dronifyit.com";

let worker = null;

/**
 * Initialize worker once (safe to call multiple times).
 */
export function initIrradianceWorker() {
  if (worker) return worker;

  worker = new Worker(new URL("./irradianceWorker.js", import.meta.url), {
    type: "module",
  });

  worker.onmessage = (e) => {
    const msg = e.data;
    const { type, segmentId } = msg;

    const cache = getOrCreateCache(segmentId);
    if (!cache) return;

    if (type === "TOF_DONE") {
      const results = msg.data;
      // we store the "first" TOF record for this segment
      const tofValue = Array.isArray(results) ? (results[0]?.TOF ?? results[0]) : (results?.TOF ?? results);
      cache.tof = tofValue;
      cache.status.tof = "done";
      cache.error = null;
    }

    if (type === "GTI_DONE") {
      const hourly = msg.data;
      cache.gti = hourly;
      cache.status.gti = "done";
      cache.error = null;
    }

    if (type === "JOB_ERROR") {
      cache.error = msg.error;
      if (msg.jobType === "FETCH_TOF") cache.status.tof = "error";
      if (msg.jobType === "FETCH_GTI") cache.status.gti = "error";
    }

    IRR_CACHE.set(segmentId, cache);

    // ✅ mirror into the segment object if it still exists
    const seg = POLYGONS.find((p) => p?.id === segmentId);
    if (seg) {
      seg.irradiance = {
        ...(seg.irradiance || {}),
        ...cache,
      };
    }
  };

  return worker;
}

/**
 * Enqueue TOF + GTI for a single segment if needed.
 * Does NOT rerun if already pending/done.
 */
export function enqueueTofGtiIfNeeded(segment, lat, lon) {
  if (!segment?.id) return;
  initIrradianceWorker();

  const cache = getOrCreateCache(segment.id);
  if (!cache) return;

  // keep segment mirrored (so any code reading segment.irradiance still works)
  segment.irradiance = { ...(segment.irradiance || {}), ...cache };

  const tilt = Number(segment?.details?.pitch || 0);
  const azimuth = Number(segment?.details?.azimu || segment?.details?.azimuth || 180);

  // TOF job
  if (cache.status.tof === "idle" || cache.status.tof === "error") {
    cache.status.tof = "pending";
    IRR_CACHE.set(segment.id, cache);
    segment.irradiance.status.tof = "pending";

    worker.postMessage({
      type: "FETCH_TOF",
      payload: {
        lidarUrl: LIDAR_URL,
        segmentId: segment.id,
        params: [
          {
            lat: Number(lat),
            lon: Number(lon),
            tilt,
            azimuth,
          },
        ],
      },
    });
  }

  // GTI job
  if (cache.status.gti === "idle" || cache.status.gti === "error") {
    cache.status.gti = "pending";
    IRR_CACHE.set(segment.id, cache);
    segment.irradiance.status.gti = "pending";

    worker.postMessage({
      type: "FETCH_GTI",
      payload: {
        lidarUrl: LIDAR_URL,
        segmentId: segment.id,
        lat: Number(lat),
        lon: Number(lon),
        tilt,
        azimuth,
      },
    });
  }
}

/**
 * Utility: ensures any segment without queued TOF/GTI gets queued.
 */
export function ensurePrecomputeForAllSegments(lat, lon) {
  for (const seg of POLYGONS) {
    if (!seg?.id) continue;
    enqueueTofGtiIfNeeded(seg, lat, lon);
  }
}
